import { useState, FormEvent } from 'react';

export default function QuickQuote() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d6ar155bd17lru29m0i0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', service: '', description: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <div className="grid md:grid-cols-5 gap-12">
            <div className="md:col-span-2">
              <h2 className="text-4xl font-serif text-gray-900 mb-4">Get Your Free Quote</h2>
              <p className="text-gray-600 mb-8">
                Fill out the form and we'll get back to you within 2 hours during business hours. Need immediate assistance? Call us now!
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                  <span>Licensed & Insured</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-star-fill text-teal-600 text-xl"></i>
                  <span>5-Star Rated</span>
                </div>
              </div>
            </div>

            <div className="md:col-span-3">
              <form onSubmit={handleSubmit} data-readdy-form id="quick-quote-form">
                <div className="space-y-6">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your Name"
                      required
                      className="w-full px-0 py-3 text-base border-0 border-b-2 border-gray-300 focus:border-teal-600 focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Phone Number"
                      required
                      className="w-full px-0 py-3 text-base border-0 border-b-2 border-gray-300 focus:border-teal-600 focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      required
                      className="w-full px-0 py-3 text-base border-0 border-b-2 border-gray-300 focus:border-teal-600 focus:outline-none transition-colors cursor-pointer"
                    >
                      <option value="">Select Service Type</option>
                      <option value="heating">Heating Installation & Repair</option>
                      <option value="cooling">AC Maintenance & Replacement</option>
                      <option value="emergency">Emergency Service</option>
                      <option value="maintenance">Annual Maintenance Plan</option>
                    </select>
                  </div>

                  <div>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of your needs"
                      rows={4}
                      maxLength={500}
                      className="w-full px-0 py-3 text-base border-0 border-b-2 border-gray-300 focus:border-teal-600 focus:outline-none transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-rose-400 text-white py-4 rounded-full text-base font-medium hover:bg-rose-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Free Quote'}
                  </button>

                  {submitStatus === 'success' && (
                    <p className="text-teal-600 text-sm text-center">Thank you! We'll contact you shortly.</p>
                  )}
                  {submitStatus === 'error' && (
                    <p className="text-red-600 text-sm text-center">Something went wrong. Please try again.</p>
                  )}

                  <p className="text-xs text-gray-500 text-center">
                    By submitting this form, you agree to our privacy policy
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
